import React, { useEffect } from 'react';
import { usePrevious } from '@mentimeter/react-hooks';
import type { ResponseText } from '@mentimeter/http-clients';
import type { Extend } from '@mentimeter/ragnar-dsc';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { ThemeBgSensitive } from '@mentimeter/ragnar-ui/themes';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';
import { useAssignedClusterStore } from '../utils/useAssignedClusterStore';
import { FadeIn } from '../motion/FadeIn';
import { useShowGroupDetailView } from '../../utils/useShowGroupDetailView';

const fullOpacityOnHover: Extend = () => ({
  '@media (hover: hover)': {
    ':hover': {
      opacity: 1,
    },
  },
});

export const NewResponsesNotification = ({
  newResponses,
}: {
  newResponses: ResponseText[] | undefined;
}) => {
  const prevNewResponses = usePrevious(newResponses);
  const {
    theme: {
      colors: { bg },
    },
  } = useRagnar();
  const translate = useTranslations('presenting.legacy');

  const {
    setPulseNewResponsesIntoView,
    setNewResponsesStatus,
    newResponsesStatus,
  } = useAssignedClusterStore((state) => state);

  const { showGroupDetailView } = useShowGroupDetailView();

  const handleClick = () => {
    setPulseNewResponsesIntoView(true);
    setTimeout(() => {
      setPulseNewResponsesIntoView(false);
    }, 1500);
  };

  useEffect(() => {
    const hasNewResponsesInitially =
      newResponsesStatus === 'initial' && newResponses?.length;
    const hasAdditionalNewResponses =
      newResponsesStatus === 'hidden' &&
      (prevNewResponses?.length ?? 0) < (newResponses?.length ?? 0);

    if (hasNewResponsesInitially || hasAdditionalNewResponses) {
      setNewResponsesStatus('shouldShow');
    }
  }, [
    newResponses,
    prevNewResponses,
    setNewResponsesStatus,
    newResponsesStatus,
  ]);

  const shouldShowButton =
    newResponsesStatus === 'shouldShow' && !showGroupDetailView.shouldShow;

  return (
    <Box
      p="space1"
      extend={() => ({
        position: 'sticky',
        left: '50%',
        transform: 'translateX(-50%)',
        height: 0,
      })}
    >
      {shouldShowButton && (
        <FadeIn delay={1000}>
          <Clickable onClick={handleClick} extend={fullOpacityOnHover}>
            <ThemeBgSensitive themeBg={bg}>
              <Box
                bg="primary"
                mt={-100}
                px="space6"
                py="space4"
                borderRadius="full"
              >
                <Text
                  fontSize="150"
                  lineHeight="175"
                  fontWeight="semiBold"
                  color="onPrimary"
                >
                  {translate('open.new_responses')}
                </Text>
              </Box>
            </ThemeBgSensitive>
          </Clickable>
        </FadeIn>
      )}
    </Box>
  );
};
